/** How a Branch or Filter step reaches the engine when its conditions span more than one record.
 *
 *  The engine reads ONE record per branch condition and evaluates ONE flat filter list (all AND or
 *  all OR) against it (trackable-api src/lib/workflowSteps/branch.ts). A step can mix contact and SMS
 *  thread conditions, and a "more than N days ago" row is internally an OR, so a step becomes one or
 *  more checks. One check serializes exactly as before; several become a chain of engine Branch
 *  nodes wired so the chain means the step's all/any. */
import type { WorkflowEdgeInput } from 'app/generated/workflows'
import type { Condition, ConditionMatch } from './conditions'
import { fieldSource } from './dataContext'

type CheckSource = `contact` | `thread` | `paperwork`

type ConditionCheck = { source: CheckSource; logic: `and` | `or`; conditions: Condition[] }

export type StepChain = { ids: string[]; match?: ConditionMatch; matchedHandle: `yes` | `pass` }

/** Engine ids after the first are `<canvasId>~<n>`; this strips that back to the canvas step. */
export const CHAIN_NODE_SUFFIX = /~\d+$/

export function splitIntoChecks(conditions: Condition[], match?: ConditionMatch): ConditionCheck[] {
  const logic = match === `any` ? `or` : `and`
  const checks: ConditionCheck[] = []
  for (const condition of conditions) {
    // Event rows are dropped (and flagged) by the serializer; a row without a field filters nothing.
    if (condition.type !== `field` || !condition.field) continue
    const layer = fieldSource(condition.field)
    const source: CheckSource = layer === `thread` || layer === `paperwork` ? layer : `contact`
    if (logic === `and` && condition.operator === `more_than_days_ago`) {
      checks.push({ source, logic: `or`, conditions: [condition] })
      continue
    }
    const shared = checks.find((check) => check.source === source && check.logic === logic)
    if (shared) shared.conditions.push(condition)
    else checks.push({ source, logic, conditions: [condition] })
  }
  return checks.length ? checks : [{ source: `contact`, logic, conditions: [] }]
}

export function chainNodeIds(canvasId: string, count: number): string[] {
  return Array.from({ length: count }, (_, index) => (index === 0 ? canvasId : `${canvasId}~${index + 1}`))
}

/** The step's engine edges once it runs as a chain. Under all, a matched check steps to the next and
 *  any miss leaves by the step's No edge; under any, a miss steps forward and any match leaves by
 *  Yes. `outgoing` is the step's canvas edges, already carrying engine handles. */
export function chainEdges(chain: StepChain, outgoing: WorkflowEdgeInput[]): WorkflowEdgeInput[] {
  const { ids, matchedHandle } = chain
  const all = chain.match !== `any`
  const last = ids.length - 1
  const links = ids.slice(1).map((target, index) => ({
    id: `${target}~in`,
    source: ids[index],
    sourceHandle: all ? matchedHandle : `default`,
    target,
    targetHandle: `in`,
  }))
  const exits = outgoing.flatMap((edge) => {
    const leavesEveryCheck = (edge.sourceHandle === matchedHandle) !== all
    const from = leavesEveryCheck ? ids.map((_, index) => index) : [last]
    return from.map((index) => ({
      ...edge,
      // An edge leaving from one check keeps its id; copies from later checks get a suffix.
      id: from.length === 1 || index === 0 ? edge.id : `${edge.id}~${index + 1}`,
      source: ids[index],
    }))
  })
  return [...links, ...exits]
}
